import connect from 'components/Shared/connect';
import { isMobile } from 'components/Shared/responsive';
import {
  getPageNumber,
  getNumberOfPages,
  getNumberOfHits,
  getProductsPerPage,
  getRootCategoryUrl
} from 'views/ProductlistPage/selectors';
import { fetchNextPage, fetchPrevPage, fetchPage } from 'views/ProductlistPage/actions';
import { NO_ELEMENT, scrollToTarget, rm } from 'utils';
import { RefObject } from 'react';
import { Paging } from 'components/Paging';

interface OwnProps {
  anchorRef?: RefObject<HTMLDivElement>;
}

interface MergedProps extends OwnProps {
  currentBreakpoint: number;
  pageNbr: number;
  nbrOfPages: number;
  nbrOfHits: number;
  productsPerPage: number;
  rootCategoryUrl: string;

  nextPage: () => Promise<void>;
  prevPage: () => Promise<void>;
  gotoPage: (page: number) => Promise<void>;
  showMoreProducts: () => Promise<void>;
}

const _ProductlistPaging = ({
  currentBreakpoint,
  nbrOfHits,
  pageNbr,
  nbrOfPages,
  productsPerPage,
  rootCategoryUrl,

  nextPage,
  prevPage,
  gotoPage,
  showMoreProducts,

  anchorRef
}: MergedProps) => {
  if (!nbrOfHits) {
    return NO_ELEMENT;
  }

  const productsSeen = Math.min(nbrOfHits, pageNbr * productsPerPage);

  const showLoadMore = pageNbr < nbrOfPages;
  const showPagePicker = nbrOfHits > productsPerPage;
  const showProgressBar = true;

  const loadMoreUrl =
    showLoadMore && isMobile({ currentBreakpoint })
      ? `${rootCategoryUrl}?page=${pageNbr + 1}`
      : undefined;

  const scrollToTopIfNeeded = scrollToTarget(anchorRef);
  const decoratedNextPage = () => nextPage().then(scrollToTopIfNeeded);
  const decoratedPrevPage = () => prevPage().then(scrollToTopIfNeeded);
  const decoratedGotoPage = (page: number) => gotoPage(page).then(scrollToTopIfNeeded);

  const progressBarDoneText = rm('productlist_you_have_seen_all_x_products', nbrOfHits);
  const progressBarText = rm('productlist_you_have_seen_x_of_y_products', productsSeen, nbrOfHits);
  const loadMoreButtonText = rm('productlist_load_more');

  return (
    <Paging
      amountOfPages={nbrOfPages}
      currentPage={pageNbr}
      gotoNextPage={decoratedNextPage}
      gotoPrevPage={decoratedPrevPage}
      gotoPage={decoratedGotoPage}
      rootCategoryUrl={rootCategoryUrl}
      showLoadMore={showLoadMore}
      showMore={showMoreProducts}
      showPagePicker={showPagePicker}
      showProgressBar={showProgressBar}
      totalItems={nbrOfHits}
      visible={productsSeen}
      loadMoreUrl={loadMoreUrl}
      loadMoreButtonText={loadMoreButtonText}
      progressBarDoneText={progressBarDoneText}
      progressBarText={progressBarText}
    />
  );
};

export const ProductlistPaging = connect<MergedProps, OwnProps>(
  ({ currentBreakpoint, currentPage }) => ({
    currentBreakpoint,
    nbrOfHits: getNumberOfHits(currentPage),
    pageNbr: getPageNumber(currentPage),
    nbrOfPages: getNumberOfPages(currentPage),
    productsPerPage: getProductsPerPage(currentPage),
    rootCategoryUrl: getRootCategoryUrl(currentPage)
  }),
  dispatch => ({
    nextPage: () => dispatch(fetchNextPage()),
    prevPage: () => dispatch(fetchPrevPage()),
    gotoPage: (page: number) => dispatch(fetchPage({ page })),
    showMoreProducts: () => dispatch(fetchNextPage({ moreProducts: true }))
  })
)(_ProductlistPaging);
